import { Stage } from "@pixi/react"
import { type CSSProperties, useCallback, useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useGraphData } from "@/domains/visualization/hooks/useGraphData"
import { selectCanvasHeight, selectCanvasWidth, selectCurrentNodeId } from "@/domains/visualization/store/selectors"
import { useGraphStore } from "@/domains/visualization/store/store"
import { QueryErrorToast } from "@/shared/components/QueryErrorToast/QueryErrorToast"
import { throttle } from "@/shared/utils/throttle/throttle"
import { GraphCanvas } from "../GraphCanvas/GraphCanvas"
import { NodePropertiesDisplay } from "../NodePropertiesDisplay/NodePropertiesDisplay"

const containerStyle: CSSProperties = {
  position: "relative",
  height: "100%",
  width: "100%",
  overflow: "hidden"
}

const stageStyle: CSSProperties = {
  width: "100%",
  height: "100%",
  position: "absolute",
  top: 0,
  left: 0
}

export function GraphContainer() {
  const { t } = useTranslation()
  const { graphData, isLoading, neighborsQuery } = useGraphData()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const canvasWidth = useGraphStore(selectCanvasWidth)
  const canvasHeight = useGraphStore(selectCanvasHeight)
  const currentNodeId = useGraphStore(selectCurrentNodeId)

  const currentNode = useMemo(() => {
    return currentNodeId ? graphData.nodes.find((node) => node.id === currentNodeId) : undefined
  }, [currentNodeId, graphData.nodes])

  const updateDimensions = useCallback(() => {
    if (wrapperRef.current) {
      const { clientWidth, clientHeight } = wrapperRef.current
      if (clientWidth > 0 && clientHeight > 0) {
        useGraphStore.getState().setCanvasDimensions(clientWidth, clientHeight)
      }
    }
  }, [])

  const throttledUpdateDimensions = useMemo(() => {
    return throttle(() => {
      updateDimensions()
    }, 500)
  }, [updateDimensions])

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      throttledUpdateDimensions()
    })

    if (wrapperRef.current) {
      observer.observe(wrapperRef.current)
    }

    return () => {
      observer.disconnect()
      throttledUpdateDimensions.cancel()
    }
  }, [throttledUpdateDimensions])

  // Cap the canvas resolution to avoid GPU overuse on high-DPI (e.g. 4K+) displays
  const cappedResolution = Math.min(window.devicePixelRatio || 1, 2)

  const stageOptions = useMemo(
    () => ({
      backgroundColor: 0xffffff,
      antialias: false,
      resolution: cappedResolution,
      autoDensity: true,
      powerPreference: "high-performance" as const
    }),
    [cappedResolution]
  )

  const errorMessage = currentNode
    ? t("visualization.error.neighboringNodesForNode", {
        nodeName: currentNode.name
      })
    : t("visualization.error.neighboringNodes")

  return (
    <>
      <div ref={wrapperRef} style={containerStyle}>
        <Stage style={stageStyle} width={canvasWidth} height={canvasHeight} options={stageOptions}>
          <GraphCanvas isLoading={isLoading} nodes={graphData.nodes} edges={graphData.edges} wrapperRef={wrapperRef} />
        </Stage>
        <NodePropertiesDisplay />
      </div>
      <QueryErrorToast
        error={neighborsQuery.error}
        isError={neighborsQuery.isError}
        refetch={() => {
          neighborsQuery.refetch()
        }}
        customMessage={errorMessage}
      />
    </>
  )
}
